import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/form/form-context";
import { predefinedTextsQueryOptions, updatePredefinedTextsFn } from "@/server/organisations";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ResponsiveActionBar, ResponsiveFormSection } from "@/components/form/responsive-form";
import { useEffect } from "react";
import { UnsavedChangesGuard } from "@/hooks/unsaved-changes-guard";
import { transformToNull, ContextUser, hasUpdatePermission } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/predefined-texts")({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    return {
      crumb: "Predefined texts",
      orgId: params.orgId,
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function RouteComponent() {
  const { orgId, user } = Route.useLoaderData();
  const maySave = hasUpdatePermission(user, "ORGANISATION");
  const predefinedTextsQuery = useQuery(predefinedTextsQueryOptions(Number(orgId)));
  const predefinedTexts = predefinedTextsQuery.data;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!orgId) return;
    void predefinedTextsQuery.refetch();
  }, [orgId, predefinedTextsQuery.refetch]);

  useEffect(() => {
    if (predefinedTexts) {
      form.reset(initiateFormValues(predefinedTexts));
    }
  }, [predefinedTexts]);

  const updatePredefinedTextsMutation = useMutation({
    mutationFn: updatePredefinedTextsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predefinedTexts"] });
    },
    onError: (error) => {
      console.error("Error updating predefined texts:", error);
    },
  });

  const updatePredefinedTexts = async (values: any) => {
    try {
      const updatePredefinedTextsPromise = updatePredefinedTextsMutation.mutateAsync({
        data: { orgId: Number(orgId), payload: values },
      });
      toast.promise(updatePredefinedTextsPromise, {
        loading: "Updating predefined texts...",
        success: (data) => {
          queryClient.invalidateQueries({ queryKey: ["predefinedTexts"] });
          return "Predefined texts updated";
        },
        error: (error) => `Failed to update predefined texts: ${error?.message}`,
      });
    } catch (error) {
      console.error("Error updating predefined texts:", error);
    }
  };

  const initiateFormValues = (values: any) => {
    return {
      shiftF1: values?.shiftF1 || null,
      shiftF2: values?.shiftF2 || null,
      shiftF3: values?.shiftF3 || null,
      shiftF4: values?.shiftF4 || null,
      shiftF5: values?.shiftF5 || null,
      shiftF6: values?.shiftF6 || null,
      shiftF7: values?.shiftF7 || null,
      shiftF8: values?.shiftF8 || null,
      shiftF9: values?.shiftF9 || null,
      shiftF10: values?.shiftF10 || null,
      shiftF11: values?.shiftF11 || null,
      shiftF12: values?.shiftF12 || null,
    };
  };

  const form = useAppForm({
    defaultValues: initiateFormValues(predefinedTexts),
    onSubmit: ({ value }) => {
      updatePredefinedTexts(preparePayload(value));
    },
  });

  const preparePayload = (value: any) => {
    const originalValues = initiateFormValues(predefinedTexts);
    let payload = Object.fromEntries(
      Object.entries(value).filter(([key, value]) => {
        if (originalValues[key as keyof typeof originalValues] === value) {
          return false;
        }
        return true;
      })
    );

    payload = transformToNull(payload);
    return payload;
  };

  const getFieldLabel = (key: string) => {
    return key.split("F").join(" + F");
  };

  return (
    <ResponsiveFormSection>
      <form.Subscribe
        selector={(state: any) => state.isDirty}
        children={(isDirty: boolean) => <UnsavedChangesGuard isDirty={isDirty} />}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="mb-4">Predefined texts</CardTitle>
            <div className="text-sm">
              Keyboard shortcuts for text that are frequently written. Use $1 for Agent First name, $2 for Agent Last
              name and $3 for Agent Sip Presentation Address.
            </div>
          </CardHeader>
          <CardContent>
            <section className="flex flex-col gap-8">
              {Object.keys(predefinedTexts || form.state.values).map((key) => (
                <form.AppField
                  key={key}
                  name={
                    key as
                      | "shiftF1"
                      | "shiftF2"
                      | "shiftF3"
                      | "shiftF4"
                      | "shiftF5"
                      | "shiftF6"
                      | "shiftF7"
                      | "shiftF8"
                      | "shiftF9"
                      | "shiftF10"
                      | "shiftF11"
                      | "shiftF12"
                  }
                  children={(field) => <field.FormTextField label={getFieldLabel(key)} id={key} />}
                />
              ))}
            </section>
          </CardContent>
        </Card>
        <form.Subscribe selector={(state) => [state.isSubmitting, state.isDirty, state.canSubmit]}>
          {([isSubmitting, isDirty, canSubmit]) => (
            <ResponsiveActionBar>
              <div className="flex gap-2">
                {maySave && (
                  <Button type="submit" disabled={isSubmitting || !isDirty || !canSubmit}>
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting || !isDirty || !canSubmit}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.reset(initiateFormValues(predefinedTexts));
                  }}
                >
                  Discard
                </Button>
              </div>
            </ResponsiveActionBar>
          )}
        </form.Subscribe>
      </form>
    </ResponsiveFormSection>
  );
}
